import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { Button } from '@openng/optimus-ui/button';
import { ButtonSize, ButtonType, ButtonVariant } from './ui-button.model';

/** Tailwind's `max-sm` breakpoint (sm = 40rem), matching its 639.98px cutoff. */
const MOBILE_QUERY = '(max-width: 639.98px)';

@Component({
  selector: 'ui-button',
  imports: [Button],
  standalone: true,
  templateUrl: './ui-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class UiButtonComponent {
  label = input<string>('');
  icon = input<string | undefined>(undefined);
  iconPos = input<'left' | 'right' | 'top' | 'bottom'>('left');
  fluid = input(false);
  variant = input<ButtonVariant>('primary');
  /** Border and label in the variant's colour, with a transparent fill. */
  outlined = input(false);
  size = input<ButtonSize>('md');
  disabled = input(false);
  loading = input(false);
  type = input<ButtonType>('button');
  /**
   * When true, the label is not passed to p-button below the sm breakpoint,
   * so the button renders as a genuine icon-only button (p-button-icon-only).
   * The label is kept as the accessible name via ariaLabel.
   */
  hideLabelOnMobile = input(false);

  private readonly mediaQuery =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY)
      : null;
  private readonly isMobile = signal(this.mediaQuery?.matches ?? false);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    const onChange = () => this.isMobile.set(this.mediaQuery?.matches ?? false);
    this.mediaQuery?.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() =>
      this.mediaQuery?.removeEventListener('change', onChange),
    );
  }

  /** Label rendered by p-button — omitted below sm when hideLabelOnMobile. */
  readonly effectiveLabel = computed(() =>
    this.hideLabelOnMobile() && this.isMobile() ? undefined : this.label(),
  );

  /** Accessible name for the suppressed label, kept on the button element. */
  readonly effectiveAriaLabel = computed(() =>
    this.hideLabelOnMobile() && this.isMobile() && this.label()
      ? this.label()
      : undefined,
  );

  /** Accessible name when the visible label is not enough (e.g. an icon button). */
  ariaLabel = input<string | undefined>(undefined);

  severity = computed(() => {
    switch (this.variant()) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'secondary';
      case 'danger':
        return 'danger';
      case 'ghost':
        return 'contrast';
      default:
        return 'primary';
    }
  });

  pSize = computed(() => {
    switch (this.size()) {
      case 'sm':
        return 'small';
      case 'lg':
        return 'large';
      default:
        return undefined;
    }
  });

  /** Accessible name: the explicit one wins, then the mobile-only fallback. */
  readonly accessibleLabel = computed(
    () => this.ariaLabel() ?? this.effectiveAriaLabel(),
  );

  isDisabled = computed(() => this.disabled() || this.loading());
}
